import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { topicSettingsServerProxy } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { usePollingContext } from 'src/providers/PollingContextProvider';

type PollConfig = {
  pollingKey: string;
  invalidateOnComplete?: boolean;
  callbackOnComplete?: () => void;
} & (
  | { type: 'topic'; topicId: string }
  | { type: 'topicGroup'; topicGroupId: string }
  | { type: 'bulkTopicGroup'; topicGroupIds: string[] }
);

/**
 * Hook for polling topic and topic group status.
 * @returns Functions for polling topic and topic group status
 */
export const useTopicPolling = () => {
  const queryClient = useQueryClient();
  const { startPolling, stopPolling } = usePollingContext();

  const startSingleStatusPolling = useCallback(
    (config: Exclude<PollConfig, { type: 'bulkTopicGroup' }>) => {
      const fetchStatus = async () => {
        const response =
          config.type === 'topic'
            ? await topicSettingsServerProxy.getTopic(config.topicId)
            : await topicSettingsServerProxy.getTopicGroup(config.topicGroupId);

        if (['Active', 'Deleted'].includes(response?.status)) {
          stopPolling(config.pollingKey);

          if (config.callbackOnComplete) {
            config.callbackOnComplete();
          }

          if (config.invalidateOnComplete) {
            queryClient.invalidateQueries(TOPIC_QUERY_KEYS.all);
          }
        }
      };

      startPolling(config.pollingKey, fetchStatus, { interval: 5000, leading: true, maxTries: 15 });
    },
    [queryClient, startPolling, stopPolling]
  );

  const startBulkStatusPolling = useCallback(
    (config: Extract<PollConfig, { type: 'bulkTopicGroup' }>) => {
      const fetchBulkStatus = async () => {
        const response = await topicSettingsServerProxy.bulkGetTopicGroups(config.topicGroupIds);

        const allComplete = Object.values(response).every((topicGroup) =>
          ['Active', 'Deleted'].includes(topicGroup.status)
        );

        if (allComplete) {
          stopPolling(config.pollingKey);
          if (config.invalidateOnComplete) {
            queryClient.invalidateQueries(TOPIC_QUERY_KEYS.all);
          }
        }
      };

      startPolling(config.pollingKey, fetchBulkStatus, { interval: 5000, leading: true, maxTries: 15 });
    },
    [queryClient, startPolling, stopPolling]
  );

  const pollTopicUpdate = useCallback(
    (topicId: string, pollingKey: string) => {
      startSingleStatusPolling({
        type: 'topic',
        topicId,
        pollingKey,
        invalidateOnComplete: true
      });
    },
    [startSingleStatusPolling]
  );

  const pollTopicGroupUpdate = useCallback(
    (topicGroupId: string, pollingKey: string, callbackOnComplete?: () => void) => {
      startSingleStatusPolling({
        type: 'topicGroup',
        topicGroupId,
        pollingKey,
        invalidateOnComplete: true,
        callbackOnComplete
      });
    },
    [startSingleStatusPolling]
  );

  const pollMultipleTopicGroupsUpdate = useCallback(
    (topicGroupIds: string[], pollingKey: string) => {
      startBulkStatusPolling({
        type: 'bulkTopicGroup',
        topicGroupIds,
        pollingKey,
        invalidateOnComplete: true
      });
    },
    [startBulkStatusPolling]
  );

  return {
    pollTopicUpdate,
    pollTopicGroupUpdate,
    pollMultipleTopicGroupsUpdate
  };
};
